import type { MessageType } from "../../pages/playground/types";

export const defaultMessages = JSON.stringify(
  [
    [
      {
        role: "system",
        content: "Assistant is a large language model trained by OpenAI.",
      },
      {
        role: "user",
        content: "Write a blog post about Generative AI",
      },
    ],
  ],
  null,
  2,
);

const getCodeListMain = (
  messages: string,
  isStream: boolean,
  model: string,
  tools: any,
  cacheControl: string,
  seed?: number | null,
) => {
  return [
    {
      type: "cURL",
      key: "curl",
      codeType: "js",
      code: `curl --location 'https://proxy-api.cogcache.com/v1/chat/completions' \\
--header 'Authorization: Bearer {COGCACHE_API_KEY}' \\
--header 'Content-Type: application/json' \\
--header 'cache-control: ${cacheControl}' \\
--data '{
    "model": "${model}",
    "stream": ${isStream},${
        seed
          ? `
    "seed": ${seed},`
          : ""
      }
    "messages": ${messages}${
        tools
          ? `,
    "tools": ${JSON.stringify(tools, null, 2)}`
          : ""
      }
}'`,
    },
    {
      type: "Python",
      key: "python",
      codeType: "js",
      code: `from openai import OpenAI

COGCACHE_LLM_MODEL = "${model}"  # the model of choice
COGCACHE_API_KEY = ""  # the generated CogCache API key

client = OpenAI(
    base_url = "https://proxy-api.cogcache.com/v1/",
    api_key = COGCACHE_API_KEY, # this is not needed here, if it's already set via environment variables
    default_headers = {
        "Authorization": f"Bearer {COGCACHE_API_KEY}",
        "cache-control": f"${cacheControl}"
    },
)

response = client.chat.completions.create(
    model = COGCACHE_LLM_MODEL,
    stream = ${isStream},${
        seed
          ? `
    seed = ${seed},
    `
          : ""
      }messages = ${messages}${
        tools
          ? `,
    tools = ${JSON.stringify(tools, null, 2)}`
          : ""
      }
   
for chunk in response:
    print(chunk)`,
    },
    {
      type: "NodeJS",
      key: "nodejs",
      codeType: "js",
      code: `import OpenAI from "openai";

const COGCACHE_LLM_MODEL = "${model}"; // the model of choice
const COGCACHE_API_KEY = ""; // the generated CogCache API key

const openai = new OpenAI({
  baseURL: "https://proxy-api.cogcache.com/v1/",
  apiKey: COGCACHE_API_KEY,
  defaultHeaders: {
    Authorization: \`Bearer \${COGCACHE_API_KEY}\`,
    "cache-control: "${cacheControl}"
  },
});

async function main() {
  try {
    const response = await openai.chat.completions.create({
      messages: ${messages},
      model: COGCACHE_LLM_MODEL,
      stream: ${isStream}${
        seed
          ? `,
      seed: ${seed}`
          : ""
      }${
        tools
          ? `,
      tools: ${JSON.stringify(tools, null, 2)},`
          : ""
      }
    });

    // Check if response is async iterable
    if (response[Symbol.asyncIterator]) {
      for await (const chunk of response) {        
        const text = chunk.choices[0]?.delta?.content; // Adjust based on actual response structure
        if (text) {
          console.log(text);
        }
      }
    } else {
      console.log('Response is not an async iterable');
    }

  } catch (error) {
    console.error('An error occurred:', error);
  }
}

main();`,
    },
  ];
};

const getCodeListLang = (
  messages: string,
  isStream: boolean,
  model: string,
  tools: any,
  cacheControl: string,
  seed?: number | null,
) => {
  const chainMessages = JSON.parse(messages).reduce(
    (acc: string, message: MessageType["choices"]) => {
      switch (message[0].role) {
        case "system":
          return (
            acc +
            `SystemMessage(content=${JSON.stringify(message[0].content)}),\n`
          );
        case "user":
          return (
            acc +
            `HumanMessage(content=${JSON.stringify(message[0].content)}),\n`
          );
        case "tool":
          return (
            acc +
            `ToolMessage(content=${JSON.stringify(message[0].content)}),\n`
          );
        default:
          return acc;
      }
    },
    "",
  );

  const chinJsMessages = JSON.parse(messages).reduce(
    (acc: string, message: MessageType["choices"]) => {
      return (
        acc +
        `new ChatMessage(${JSON.stringify(message[0].content)}, "${
          message[0].role
        }"),\n`
      );
    },
    "",
  );

  return [
    {
      type: "LangChain",
      key: "langchain",
      codeType: "js",
      code: `from langchain_openai import ChatOpenAI
from langchain.schema import HumanMessage, SystemMessage

COGCACHE_LLM_MODEL = "${model}" # the model of choice
COGCACHE_API_KEY = ""  # the generated CogCache API key

model = ChatOpenAI(
    base_url = "https://proxy-api.cogcache.com/v1/",
    model = COGCACHE_LLM_MODEL,
    streaming = ${isStream},
    openai_api_key = COGCACHE_API_KEY,${
      seed
        ? `
    seed = ${seed},`
        : ""
    }
    default_headers = {
        "Authorization": f"Bearer {COGCACHE_API_KEY}"
        "cache-control": f"${cacheControl}"
    },
)${
        tools?.length
          ? `
tools = ${JSON.stringify(tools, null, 2)}}
model_with_tools = model.with_structured_output(tools)
`
          : ""
      }

response = ${tools?.length ? "model_with_tools" : "model"}.stream(
    [
        ${chainMessages}
    ],
)

for chunk in response:
    print(chunk.content)`,
    },
    {
      type: "LangChain JS",
      key: "langchainjs",
      codeType: "js",
      code: `import { ChatOpenAI } from "langchain/chat_models/openai";
import { ChatMessage } from "langchain/schema";

const COGCACHE_LLM_MODEL = "${model}"; // the Azure OpenAI model of choice
const COGCACHE_API_KEY = ""; // the generated CogCache API key

const AZURE_API_VERSION = ""; // the Azure OpenAI version of your choice

const model = new ChatOpenAI({
  streaming: ${isStream},
  azureOpenAIApiKey: COGCACHE_API_KEY,
  azureOpenAIApiVersion: AZURE_API_VERSION,
  azureOpenAIApiDeploymentName: COGCACHE_LLM_MODEL,${
    seed
      ? `
  seed: ${seed},`
      : ""
  }
  azureOpenAIBasePath: "https://proxy-api.cogcache.com/azure",
  configuration: {
    baseOptions: {
      headers: {
        "Cogcache-Auth": "Bearer \$\{COGCACHE_API_KEY\}",
        "cache-control": "${cacheControl}"
      },
    },
  },
});${
        tools?.length
          ? `
const tools = ${JSON.stringify(tools, null, 2)}
const modelWithTools = model.bindTools(tools);
`
          : ""
      }
(async () => {
  const response = await ${tools?.length ? "model" : "modelWithTools"}.invoke(
    [
      ${chinJsMessages}
    ],
    {
      callbacks: [
        {
          handleLLMNewToken: (token, ...resp) => {
            console.log("handleLLMNewToken", token);
          },
          handleLLMEnd: async output => {
            console.log("handleLLMEnd", output);
          },
          handleChatModelStart: (...chat) => {
            console.log("handleChatModelStart", chat);
          },
        },
      ],
    },
  );
})().catch(err => {
  console.error("Something went wrong:", err);
});`,
    },
  ];
};

export const getCodeList = (
  messages: string = defaultMessages,
  isStream: boolean,
  model: string,
  tools: any,
  cacheControl = "public",
  seed?: number | null,
) => {
  const main = getCodeListMain(
    messages,
    isStream,
    model,
    tools?.length ? tools : null,
    cacheControl,
    seed,
  );
  const lang = getCodeListLang(
    messages,
    isStream,
    model,
    tools?.length ? tools : null,
    cacheControl,
    seed,
  );
  return {
    main,
    lang,
  };
};
